<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { extractVariables } from '@chatwoot/utils';

import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// Plain body text, e.g. "Hello {{1}}, your order {{2}} is ready." - the
// exact same {{n}} convention WhatsAppTemplateParser.vue/TemplateProcessorService
// already parse at send time, so a template authored here is guaranteed to
// be readable by the existing sending path without any new format.
const body = defineModel('body', { type: String, default: '' });

const { t } = useI18n();

// extractVariables is the same @chatwoot/utils helper the mobile app and
// WhatsAppTemplateParser.vue rely on - not a second parser.
const variables = computed(() => extractVariables(body.value));

// Built here rather than inline in the template - a template literal
// containing literal `{{`/`}}` sequences reads as deeply ambiguous next to
// Vue's own `{{ }}` mustache delimiters.
const detectedVariablesLabel = computed(() =>
  variables.value.map(name => `{{${name}}}`).join(', ')
);

// Appends at the end rather than at the cursor - TextArea.vue doesn't
// expose its underlying <textarea> ref, so cursor-position insertion isn't
// reliably achievable without reaching into its internals.
const insertVariable = () => {
  const nextNumber = variables.value.length + 1;
  body.value = `${body.value}{{${nextNumber}}}`;
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center justify-between">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('WHATSAPP_TEMPLATES.BUILDER.BODY.LABEL') }}
      </label>
      <Button
        :label="t('WHATSAPP_TEMPLATES.BUILDER.BODY.INSERT_VARIABLE')"
        variant="link"
        size="sm"
        icon="i-lucide-plus"
        @click="insertVariable"
      />
    </div>
    <!-- max-length mirrors Whatsapp::TemplateManagementService::BODY_MAX_LENGTH -->
    <TextArea
      v-model="body"
      :placeholder="t('WHATSAPP_TEMPLATES.BUILDER.BODY.PLACEHOLDER')"
      :max-length="1024"
      show-character-count
      auto-height
      min-height="6rem"
    />
    <p v-if="variables.length" class="text-xs text-n-slate-11">
      {{
        t('WHATSAPP_TEMPLATES.BUILDER.BODY.VARIABLES_DETECTED', {
          variables: detectedVariablesLabel,
        })
      }}
    </p>
  </div>
</template>
